import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Settings as SettingsIcon, Users, ListChecks,
  Clock, Wallet, LogOut, Menu, X, CalendarDays,
} from "lucide-react";
import CalendarView from "@/components/CalendarView";

function RealtimeClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const wib = new Date(now.getTime() + (now.getTimezoneOffset() + 420) * 60000);
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <div data-testid="header-realtime-clock" className="text-right">
      <div className="font-mono text-[var(--gold)] text-lg font-semibold tracking-wide">
        {pad(wib.getHours())}:{pad(wib.getMinutes())}:{pad(wib.getSeconds())} <span className="text-xs text-white/50">WIB</span>
      </div>
      <div className="text-[11px] text-white/60">
        {days[wib.getDay()]}, {wib.getDate()} {months[wib.getMonth()]} {wib.getFullYear()}
      </div>
    </div>
  );
}

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link", roles: ["SuperAdmin", "SPV", "Leader", "Staff"], end: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings-link", roles: ["SuperAdmin", "SPV", "Leader"] },
  { to: "/clients", label: "Client Onboarding", icon: Users, testid: "nav-client-onboarding-link", roles: ["SuperAdmin", "SPV", "Leader", "Staff"] },
  { to: "/tracker", label: "Project Tracker", icon: ListChecks, testid: "nav-project-tracker-link", roles: ["SuperAdmin", "SPV", "Leader", "Staff"] },
  { to: "/attendance", label: "Attendance", icon: Clock, testid: "nav-attendance-link", roles: ["SuperAdmin", "SPV", "Leader", "Staff"] },
  { to: "/payroll", label: "Payroll & Tax", icon: Wallet, testid: "nav-payroll-link", roles: ["SuperAdmin", "SPV"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showCal, setShowCal] = useState(false);
  const items = NAV.filter((n) => n.roles.includes(user?.role));

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const roleColor = { SuperAdmin: "#C5A059", SPV: "#C5A059", Leader: "#0D9488", Staff: "#525866" }[user?.role] || "#525866";

  return (
    <div className="min-h-screen flex flex-col">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-[1500px] mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg gold-gradient flex items-center justify-center font-serif font-bold text-lg text-[#111318]">S</div>
            <div>
              <div className="font-serif text-white text-lg leading-none tracking-wide">SAVIOR</div>
              <div className="text-[10px] text-white/50 tracking-widest">PRIME INDONESIA</div>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <button data-testid="header-calendar-button" onClick={() => setShowCal(true)} className="flex items-center gap-2 hover:opacity-80 transition" title="Open calendar">
              <CalendarDays className="w-4 h-4 text-[var(--gold)]" />
              <RealtimeClock />
            </button>
            <div className="hidden sm:flex items-center gap-3 pl-6 border-l border-white/10">
              <div className="text-right">
                <div className="text-white text-sm font-medium">{user?.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: roleColor }}>
                  {user?.id_code} · {user?.role}
                </div>
              </div>
              <div className="w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm text-[#111318]" style={{ background: roleColor }}>
                {user?.name?.[0]}
              </div>
              <button data-testid="logout-button" onClick={handleLogout} className="text-white/60 hover:text-[var(--gold)] transition ml-1">
                <LogOut className="w-5 h-5" />
              </button>
            </div>
            <button className="lg:hidden text-white" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        <nav className="border-t border-white/10">
          <div className={`max-w-[1500px] mx-auto px-6 ${mobileOpen ? "flex flex-col py-2" : "hidden lg:flex"} lg:flex items-center gap-7`}>
            {items.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testid}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `nav-item flex items-center gap-2 py-3 text-sm font-medium ${isActive ? "active text-[var(--gold)]" : "text-white/70 hover:text-white"}`
                }
              >
                <n.icon className="w-4 h-4" /> {n.label}
              </NavLink>
            ))}
          </div>
        </nav>
      </header>

      <main className="flex-1 max-w-[1500px] w-full mx-auto px-6 py-8">
        <Outlet />
      </main>
      {showCal && <CalendarView onClose={() => setShowCal(false)} />}
    </div>
  );
}
